import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import App from './App';
import logger from './utils/logger';
import { ErrorFallback } from './components/ErrorFallback';
import { initSentry } from './lib/sentry-init';

// Load browser console scripts
import './scripts/index';

// Import platform color variables
import './styles/platforms.css';

// Initialize Sentry FIRST (before React mount).
// Issue #855: defensive init to prevent silent failures.
// Issue #3601: .trim() + post-init getDsn() guard against Vercel env contamination (#3420).
initSentry(import.meta.env);

// Initialize PostHog Analytics (Issue #858)
// Only enabled in Production environment for clean debugging in development
const posthogKey = import.meta.env.VITE_POSTHOG_KEY?.trim();
const posthogHost = import.meta.env.VITE_POSTHOG_HOST?.trim();
const isProduction = import.meta.env.VITE_VERCEL_ENV === 'production';

// Issue #1424: Validate PostHog API key to prevent 401 errors
// - Must exist and not be the string "undefined" (from JSON.stringify(undefined))
// - Must start with "phc_" (valid PostHog project API key format)
const isValidPosthogKey =
  posthogKey &&
  posthogKey !== 'undefined' &&
  typeof posthogKey === 'string' &&
  posthogKey.startsWith('phc_');

if (isValidPosthogKey && isProduction) {
  try {
    posthog.init(posthogKey, {
      api_host: posthogHost || 'https://us.i.posthog.com',
      capture_pageview: true,
      capture_pageleave: true,
      persistence: 'localStorage',
      // Respect user privacy
      opt_out_capturing_by_default: false,
      // Disable in localhost for cleaner debugging
      loaded: (posthogInstance) => {
        if (import.meta.env.DEV) {
          posthogInstance.opt_out_capturing();
        }
      },
    });
    console.log('✅ PostHog analytics enabled (Production)');
  } catch (error) {
    // Issue #1320: Analytics failure should not block app functionality
    console.warn('⚠️ PostHog initialization failed:', error);
  }
} else if (isProduction && !isValidPosthogKey) {
  // Issue #1424: Log warning when key is missing/invalid in production
  console.warn(
    '⚠️ PostHog API key not configured or invalid - analytics disabled'
  );
  console.warn(
    'Set VITE_POSTHOG_KEY in Vercel env vars (must start with "phc_")'
  );
} else if (!isProduction) {
  console.log('ℹ️ PostHog analytics disabled (non-production environment)');
}

// Debug logging
logger.log('Main.tsx loading...');
logger.log('React mounting started');

try {
  const rootElement = document.getElementById('root');
  logger.log('Root element found:', rootElement);

  if (!rootElement) {
    throw new Error('Root element not found');
  }

  const root = ReactDOM.createRoot(rootElement);
  logger.log('Root created successfully');

  // StrictModeを有効化（開発時の問題検出のため）
  // Sentry ErrorBoundaryでアプリ全体をラップ（Issue #481 Phase 1）
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
        <App />
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );

  logger.log('React mounting completed');
} catch (error) {
  logger.error('Error mounting React app:', error);
  document.getElementById('root')!.innerHTML = `
    <div style="color: red; padding: 20px;">
      <h1>Error loading application</h1>
      <pre>${error}</pre>
    </div>
  `;
}
